<template>
  <teleport to="body">
    <div class="fixed top-4 right-4 z-50 space-y-2 max-w-lg w-full">
      <transition-group
        name="notification"
        tag="div"
        class="space-y-2"
      >
        <div
          v-for="notification in notifications"
          :key="notification.id"
          :class="[
            'w-full bg-white dark:bg-gray-800 shadow-lg rounded-lg pointer-events-auto overflow-hidden border border-gray-200 dark:border-gray-700',
            'transform transition-all duration-300 ease-in-out',
            notification.show ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
          ]"
        >
          <div class="p-4">
            <div class="flex items-start">
              <div class="flex-shrink-0">
                <CheckCircleIcon v-if="notification.type === 'success'" class="h-6 w-6 text-green-400" />
                <XCircleIcon v-else-if="notification.type === 'error'" class="h-6 w-6 text-red-400" />
                <ExclamationTriangleIcon v-else-if="notification.type === 'warning'" class="h-6 w-6 text-yellow-400" />
                <InformationCircleIcon v-else class="h-6 w-6 text-blue-400" />
              </div>
              <div class="ml-3 w-0 flex-1 pt-0.5">
                <p class="text-sm font-medium text-gray-900 dark:text-white">
                  {{ notification.message }}
                </p>
                <!-- Action buttons -->
                <div v-if="notification.actions" class="mt-3 flex flex-wrap gap-2">
                  <button
                    v-for="action in notification.actions"
                    :key="action.text"
                    @click="handleAction(action, notification.id)"
                    class="text-xs bg-primary-600 text-white px-3 py-1.5 rounded hover:bg-primary-700 transition-colors font-medium"
                  >
                    {{ action.text }}
                  </button>
                </div>
              </div>
              <div class="ml-4 flex-shrink-0 flex">
                <button
                  @click="removeNotification(notification.id)"
                  class="bg-white dark:bg-gray-800 rounded-md inline-flex text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  <XMarkIcon class="h-5 w-5" />
                </button>
              </div>
            </div>
          </div>
          <div 
            :class="[
              'h-1 transition-all duration-300 ease-linear',
              notification.type === 'success' ? 'bg-green-400' : '',
              notification.type === 'error' ? 'bg-red-400' : '',
              notification.type === 'warning' ? 'bg-yellow-400' : '',
              notification.type === 'info' ? 'bg-blue-400' : ''
            ]"
            :style="{ width: `${(notification.duration - Date.now() + parseInt(notification.id)) / notification.duration * 100}%` }"
          ></div>
        </div>
      </transition-group>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { useNotifications } from '@/composables/useNotifications';
import type { NotificationAction } from '@/composables/useNotifications';
import {
  CheckCircleIcon,
  XCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XMarkIcon,
} from '@heroicons/vue/24/outline';

const { notifications, removeNotification } = useNotifications();

const handleAction = (action: NotificationAction, notificationId: string) => {
  action.action();
  removeNotification(notificationId);
};
</script>

<style scoped>
.notification-enter-active,
.notification-leave-active {
  transition: all 0.3s ease;
}

.notification-enter-from {
  opacity: 0;
  transform: translateX(100%);
}

.notification-leave-to {
  opacity: 0;
  transform: translateX(100%);
}
</style>